import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminValue } from "@/modules/recognition/admin/types";

export type ValueList = Paginate<AdminValue<true>>;

export const values = createQueryKeys("admin-recognition-values", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "attributes";

export const fetchValuesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...values.all(page, search),
    queryFn: () =>
      adminApi
        .get<ValueList>(`/recognition/values`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchValues = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchValuesQueryOptions(page, search));

export const fetchValueQueryOptions = (id?: number) =>
  queryOptions({
    ...values.one(id),
    queryFn: () =>
      adminApi
        .get<AdminValue<true>>(`/recognition/values/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchValue = (id?: number) =>
  useQuery(fetchValueQueryOptions(id));

export interface ValueRequest {
  name: string;
  color: string;
  description: string | null;
  attributes: Array<{
    id: number | undefined;
    name: string;
  }>;
}

export const useCreateValue = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ValueRequest) =>
      adminApi
        .post("/recognition/values/", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: values.all._def,
      });
    },
  });
};

export const useUpdateValue = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ValueRequest) =>
      adminApi
        .put(`/recognition/values/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: values.all._def,
      });
      queryClient.invalidateQueries(values.one(id));
    },
  });
};

export const useCreateOrUpdateValue = (id: number | undefined) => {
  const create = useCreateValue();
  const update = useUpdateValue(id!);
  return id ? update : create;
};

export const useDeleteValue = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`recognition/values/${id}`),
    onSuccess: (_, id: number) => {
      queryClient.invalidateQueries({
        queryKey: values.all._def,
      });
      queryClient.invalidateQueries(values.one(id));
    },
  });
};
